import { Box, Colors, Text } from '@fozito/common-ui';
import { AutoComplete, Form, Input, InputProps, InputRef, Row, Spin } from 'antd';
import clsx from 'clsx';
import { debounce, delay, first } from 'lodash';
import { ISearchCustomerItem } from 'modules/customer/widgets/AddCustomer/type';
import cashierService from 'modules/POS/services';
import cashierApis from 'modules/POS/services/apis';
import { IProductItem } from 'modules/shop/types/product';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import styled from 'styled-components';
import { formatCurrency, formatNumber } from 'utils/formatCurrency';
import { validate as validateUUID } from 'uuid';
import qr_scan_icon from './QR-scan.svg';
import { DollarAmountInputRef } from '../../components/DollarAmount/helpers';

export const unknownCustomerId = '_unknownCustomerId';

// @ts-ignore
export const unknownCustomerItem: ISearchCustomerItem = {
  id: unknownCustomerId,
  email: '',
  name: 'Unknown Customer',
  phone: '0000000000',
  numberVisit: 0,
  point: 0,
};
export const maskPhone = (val: string) => {
  if (!val) return '';
  return '(***) ***-' + val.slice(val.length - 4, val.length);
};
export type ISearchCustomer = {
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerPoint: number;
};
type Ref = {
  focus: () => void;
};
type ProductNameInputProps = {
  v2NewApt?: boolean;
  value?: string;
  onKeyDown?: InputProps['onKeyDown'];
  onChange?: (value: string) => void;
  inputPriceRef?: React.RefObject<DollarAmountInputRef>

};
export const useProductNameInputRef = () => useRef<Ref>(null);
const ProductNameInput = forwardRef<Ref, ProductNameInputProps>(({ inputPriceRef, onKeyDown = () => undefined, value, onChange = () => undefined }, ref) => {
  const form = Form.useFormInstance();
  const inputRef = useRef<InputRef>(null);
  const [customersFilter, setCustomersFilter] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const products = cashierService.selectors.products();
  const fromScan = useRef(false);
  const initialOptions = useMemo(() => products?.map(o => {
    return ({
      value: o.id,
      name: o.productName,
      label: renderLabel(o),
      price: o.sellPrice,
      discount: o.discount,
      keywords: [o.productName?.toLocaleLowerCase(), o.productCode],
    });
  }), [products]);

  const [searchText, setSearchText] = useState<string>('');

  const optionsFilter = useMemo(() => customersFilter?.map(o => {
    return ({
      value: o.id,
      name: o.productName,
      label: renderLabel(o),
      price: o.sellPrice,
      discount: o.discount,
      keywords: [o.productName?.toLocaleLowerCase(), o.productCode],
    });
  }) || [], [customersFilter]);

  const options = useMemo(() => {
    if (!searchText) return initialOptions;
    return optionsFilter.concat([{
      discount: 0, keywords: [], label: <Box height={32} alignItems='center' display='flex' justifyContent='center' gap={8}>
        <Text body_body_1_14_regular>use product name: </Text>
        <Text>{searchText}</Text>
      </Box>, name: searchText, price: 0, value: searchText
    }]);
  }, [initialOptions, searchText, optionsFilter]);

  const handleSwitchCustomer = (data: string) => {
    delay(() => inputPriceRef?.current?.focus(), 100);
    if (data === searchText) {
      form.setFieldValue('pricePerPound', 0);
      return onChange(searchText);
    }
    setSearchText('');
    const result: IProductItem = (searchText ? customersFilter : products)?.find((item) => item.id === data);
    if (!result?.id) return;
    onChange(result.productName);
    form.setFieldValue('product', result);
    form.setFieldValue('pricePerPound', getPrice(result));
    form.setFieldValue('productName', result.productName);
  };

  const getList = useCallback(debounce(async (search: string) => {
    if (!search?.trim()) return setLoading(false);
    if (validateUUID(search)) return setLoading(false);
    try {
      const res: IResponseData<IProductItem[]> = await cashierApis.getProductByCode(search);
      if (res.data.data) {
        if (fromScan.current) {
          const result = first(res.data.data);
          if (result?.productCode === search) {
            setSearchText(result.productName);
            form.resetFields();
            form.setFieldValue('product', result);
            form.setFieldValue('pricePerPound', getPrice(result));
            form.setFieldValue('productName', result.productName);
          }
        } else {
          setCustomersFilter(res.data.data);
        }
      }
    } catch (error) { }
    finally {
      setLoading(false);
      fromScan.current = false;
    }
  }, 500), []);

  const onClearSearch = () => {
    form.resetFields(['product', 'productName']);
    setSearchText('');
    delay(() => inputRef?.current?.focus(), 100);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown(event);
    if (event.key === 'Enter') {
      event.preventDefault();
      fromScan.current = true;
      return;
    }
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      delay(() => inputRef?.current?.focus(), 100);
    }
  }));
  console.log('searchText,searchText', value);

  return (
    <Container wrap={false} align={'middle'} style={{ gap: '4px' }} className='CustomerSearchInput'>
      <div className={clsx('container-input', (!!searchText || !!value) && 'visible')}>
        <Input
          ref={inputRef}
          value={searchText}
          placeholder={value}
          allowClear
          onClear={onClearSearch}
          onKeyDown={handleKeyDown}
          onChange={e => {
            const text = e.target.value;
            fromScan.current = false;
            if (text)
              setLoading(true);
            setSearchText(text);
            getList(text);
          }}
          suffix={<QRScanIcon onClick={() => inputRef?.current?.focus()}> <img src={qr_scan_icon} alt="qr_scan_icon" style={{ height: 64, width: 'auto' }} /></QRScanIcon>}
        />
      </div>
      <div className="container-input">
        <AutoComplete
          style={{ width: '100%' }}
          options={options}
          dropdownRender={(menu) => (<>
            {!!searchText && loading ? <Row align={'middle'} justify={'center'}><Text><Spin /></Text></Row> : menu}
          </>)}
          onSelect={handleSwitchCustomer}
          value={searchText}
          open={!!searchText}
          onClear={onClearSearch}
          onDropdownVisibleChange={(_value) => {
            if (!_value) {
              setSearchText('');
            }
          }}
          rootClassName='zoom-ui'
        ><input style={{ display: 'none' }} type="text" /></AutoComplete>
      </div>
    </Container>
  );
});
ProductNameInput.displayName = 'ProductNameInput';
export default ProductNameInput;

const Container = styled(Row)`
display: flex;
flex-direction: column;
justify-content: center;
align-items: center;
align-self: stretch;

.container-input {
  &.visible {
    .ant-input-clear-icon {
      visibility: visible !important;
      svg {
        height: 30px;
        width: 30px;
      }
    }
  }
}

  .ant-select-selection-placeholder, input.ant-input {
    font-family: Poppins;
    font-size: 40px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    padding-left: 16px;
    margin-left: 105px;
  }
  
  .container-input {
    display: flex;
    flex: 1;
    align-items: center;
    position: relative;
    align-self: stretch;
    input.ant-input::placeholder {
      color: #1D2129;
    }
  }
  .ant-select-single {
    height: unset;
  }
  .ant-input-affix-wrapper {
    background: transparent;
    box-shadow: none;
    border: none;
    padding: 0;
    overflow: hidden;
  }
  .ant-select-selector {
    background: #FFF;
    border: none;
  }
}
`;
const DiscountText = styled(Text)`
color: #D2464F;
font-style: normal;
line-height: normal;
text-decoration-line: strikethrough;
text-transform: capitalize;
text-decoration: line-through;
`;
const getPrice = (item: IProductItem) => {
  if (!item.discount) return item.sellPrice;

  return item.sellPrice - item.discount;
};
const renderPrice = (item: IProductItem) => {
  if (!item.discount) return (
    <div>
      <Text>
        {formatCurrency(item.sellPrice)}
      </Text>
      <Text body_body_1_14_regular color_text_4 text_right>
        {formatNumber(item.inventory)} left
      </Text>
    </div>
  );

  return <>
    <div>
      <Box display='flex' alignItems='center' gap={4}>
        <DiscountText color={Colors.primary[6]}>
          {formatCurrency(item.sellPrice)}
        </DiscountText>
        <Text>
          {formatCurrency(item.sellPrice - item.discount)}
        </Text>
      </Box>
      <Text body_body_1_14_regular color_text_4 text_right>
        {formatNumber(item.inventory)} left
      </Text>
    </div>
  </>;
};
const renderLabel = (o: IProductItem) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
      }}
    >
      <Text>
        {o.productName}
      </Text>
      <Text body_body_1_14_regular color_text_4>
        CODE: {o.productCode}
      </Text>
    </div>
    <Box display='flex' alignItems='center' gap={4}>
      {renderPrice(o)}
    </Box>
  </div>
);

const QRScanIcon = styled.div`
display: flex;
width: 80px;
height: 80px;
justify-content: center;
align-items: center;
border-radius: 0px 4px 4px 0px;
background: #E5E6EB;
`;
